import React, { useEffect, useState, useRef, useCallback } from 'react'
import { ReactComponent as Cross } from "../../assets/img/cross.svg"
import { Card, CardBody, Table } from 'reactstrap';
import api from 'api';
import Spinner from 'components/Spinner/Spinner';
import moment from 'moment';

// Format E.164 phone numbers for display. Mongo stores +19548015997;
// brand admins want to read it at a glance. US numbers (+1 + 10 digits)
// render as (954) 801-5997. Other countries get a space after the +CC.
const formatPhone = (raw) => {
    if (!raw) return '';
    const s = String(raw).trim();
    const cleaned = s.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+1') && cleaned.length === 12) {
        return `(${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
    }
    if (cleaned.startsWith('+') && cleaned.length > 4) {
        return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    }
    return s;
};

// Some legacy scanner records have a phone string in the email field.
// Without this guard the modal header would render the same number twice.
const looksLikePhone = (raw) => {
    if (!raw) return false;
    const cleaned = String(raw).replace(/[^\d+]/g, '');
    return /^\+?\d{10,15}$/.test(cleaned);
};

// Map an EmailLog row's `type` enum to a human-readable label + the
// outward-facing description of who got the email.
const EMAIL_TYPE_LABEL = {
    order_placed: 'Order confirmation email',
    order_fulfilled: 'Order fulfilled email',
    order_brand_notification: 'Brand notified of order',
};

const PAGE_SIZE = 50;

const ViewPointHistory = ({ handleModal, brandId, viewPointHistory }) => {
    const [allocations, setAllocations] = useState([]);
    const [otherEvents, setOtherEvents] = useState([]); // redeems + logs + emails
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [allocPage, setAllocPage] = useState(1);
    const [hasMoreAllocs, setHasMoreAllocs] = useState(false);
    const [allocTotal, setAllocTotal] = useState(0);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const scrollRef = useRef(null);

    // Sortable activity table
    const [actSort, setActSort] = useState({ key: 'date', dir: 'desc' });
    const toggleActSort = (key) => {
        setActSort((prev) => {
            if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            const numericOrDate = new Set(['date', 'points']);
            return { key, dir: numericOrDate.has(key) ? 'desc' : 'asc' };
        });
    };

    // Merge allocations + other events into a single sorted list
    const activity = React.useMemo(() => {
        const allocEvents = allocations.map((a) => ({
            kind: 'earned',
            _id: a._id,
            date: a.allocatedAt || a.createdAt,
            label: a.productName || 'Scan',
            points: Number(a.points) || 0,
            raw: a,
        }));
        return [...allocEvents, ...otherEvents]
            .filter((e) => e.date)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [allocations, otherEvents]);

    const sortedActivity = React.useMemo(() => {
        const copy = activity.slice();
        const dir = actSort.dir === 'asc' ? 1 : -1;
        const getValue = (e) => {
            switch (actSort.key) {
                case 'kind': return String(e?.kind || '');
                case 'item': return String(e?.label || '').toLowerCase();
                case 'date': return e?.date ? new Date(e.date).getTime() : 0;
                case 'points': return Number.isFinite(Number(e?.points)) ? Number(e.points) : 0;
                default: return 0;
            }
        };
        copy.sort((a, b) => {
            const av = getValue(a);
            const bv = getValue(b);
            if (av === bv) return 0;
            return (av < bv ? -1 : 1) * dir;
        });
        return copy;
    }, [activity, actSort]);

    // Load allocations for a specific page
    const loadAllocPage = useCallback((page) => {
        if (!viewPointHistory?._id || !brandId) return Promise.resolve();
        return api('get', `/scanners/allAllocationHistory?userId=${viewPointHistory._id}&brandId=${brandId}&page=${page}&pageSize=${PAGE_SIZE}`)
            .then((res) => {
                const newAllocs = res?.allocationHistory || [];
                if (page === 1) {
                    setAllocations(newAllocs);
                } else {
                    setAllocations((prev) => [...prev, ...newAllocs]);
                }
                setHasMoreAllocs(!!res?.hasMore);
                setAllocTotal(res?.total || 0);
                setAllocPage(page);
            });
    }, [viewPointHistory?._id, brandId]);

    // Initial load — fetch page 1 of allocations + all other streams
    useEffect(() => {
        if (!viewPointHistory?._id || !brandId) return;
        setLoading(true);
        Promise.all([
            loadAllocPage(1),
            api('get', `/scanners/allAredeemhistoryHistory?userId=${viewPointHistory._id}&brandId=${brandId}`),
            api('get', `/scanners/scannerActivity?userId=${viewPointHistory._id}&brandId=${brandId}`),
        ])
            .then(([, redeemRes, actRes]) => {
                const redeems = (redeemRes?.redeemHistory || redeemRes || []).map((r) => ({
                    kind: 'redeemed',
                    _id: r._id,
                    date: r.redeemedAt || r.createdAt,
                    label: r.productId?.productName || 'Reward redeemed',
                    points: -(Number(r.pointsUsed) || 0),
                    raw: r,
                }));
                const logs = (actRes?.userLogs || []).map((l) => {
                    const t = String(l.emailType || '').trim();
                    const isSignIn = /sign.?in|login/i.test(t);
                    const isWelcome = /welcome bonus/i.test(t);
                    return {
                        kind: isSignIn ? 'signin' : isWelcome ? 'bonus' : 'event',
                        _id: l._id,
                        date: l.createdAt,
                        label: isSignIn ? 'Signed in' : isWelcome ? 'Welcome bonus granted' : t || 'Account event',
                        points: isWelcome ? Number(l?.metadata?.bonusPoints) || 0 : null,
                        raw: l,
                    };
                });
                const emails = (actRes?.emailLogs || []).map((e) => ({
                    kind: 'email',
                    _id: e._id,
                    date: e.sentAt || e.createdAt,
                    label: EMAIL_TYPE_LABEL[e.type] || 'Email sent',
                    points: null,
                    raw: e,
                }));
                setOtherEvents([...redeems, ...logs, ...emails]);
            })
            .catch((err) => console.log(err))
            .finally(() => setLoading(false));
    }, [viewPointHistory?._id, brandId, loadAllocPage]);

    // Load more allocations
    const loadMore = useCallback(() => {
        if (loadingMore || !hasMoreAllocs) return;
        setLoadingMore(true);
        loadAllocPage(allocPage + 1)
            .catch((err) => console.log(err))
            .finally(() => setLoadingMore(false));
    }, [loadingMore, hasMoreAllocs, allocPage, loadAllocPage]);

    // Infinite scroll — trigger loadMore when user scrolls near bottom
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const handleScroll = () => {
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
                loadMore();
            }
        };
        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);
    }, [loadMore]);

    // Visual chip for each event kind.
    const chipFor = (kind) => {
        switch (kind) {
            case 'earned':   return { label: 'Scan',     bg: '#dcfce7', fg: '#079455' };
            case 'redeemed': return { label: 'Redeem',   bg: '#fef2f2', fg: '#D92D20' };
            case 'signin':   return { label: 'Sign-in',  bg: '#eef2ff', fg: '#4338CA' };
            case 'bonus':    return { label: 'Bonus',    bg: '#fef3c7', fg: '#B45309' };
            case 'email':    return { label: 'Email',    bg: '#e0f2fe', fg: '#0369A1' };
            default:         return { label: 'Event',    bg: '#f3f4f6', fg: '#4b5563' };
        }
    };

    return (
        <div style={{ fontFamily: "Outfit", position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
            <div ref={scrollRef} className="modal-box" style={{ backgroundColor: "#fff", borderRadius: "15px", boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)", padding: "25px", position: "relative", maxWidth: "640px", width: "100%", maxHeight: "85vh", overflowY: "auto" }}>

                {/* Header */}
                <h1 style={{ fontWeight: 700, fontSize: "22px", color: "#1C1C1C", marginBottom: "15px" }}>Customer activity</h1>

                {/* Close Button */}
                <div onClick={handleModal} className="position-absolute" style={{ right: "20px", top: "20px", cursor: "pointer" }}>
                    <Cross />
                </div>

                {/* User Info Card */}
                <Card style={{ backgroundColor: "#F5F5F5", borderRadius: "8px", padding: "12px", marginBottom: "15px" }}>
                    <CardBody style={{ padding: "8px" }}>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: "16px" }}>{viewPointHistory?.name || '—'}</p>
                        {viewPointHistory?.email && !looksLikePhone(viewPointHistory.email) && (
                            <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>{viewPointHistory.email}</p>
                        )}
                        {viewPointHistory?.phone && (
                            <p style={{ margin: 0, fontSize: "14px", color: "#666", fontVariantNumeric: "tabular-nums" }}>
                                <a href={`tel:${viewPointHistory.phone}`} style={{ color: "#666", textDecoration: "none" }}>{formatPhone(viewPointHistory.phone)}</a>
                            </p>
                        )}
                        {allocTotal > 0 && (
                            <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#362FD8', fontWeight: 600 }}>
                                {allocTotal} total scan{allocTotal !== 1 ? 's' : ''}
                            </p>
                        )}
                    </CardBody>
                </Card>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <Table responsive style={{ marginTop: "10px", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    {(() => {
                                        const Indicator = ({ active, dir }) => {
                                            if (!active) return (
                                                <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='#cbd5e1' strokeWidth='2.5' style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden='true'>
                                                    <polyline points='8 9 12 5 16 9' /><polyline points='8 15 12 19 16 15' />
                                                </svg>
                                            );
                                            return dir === 'asc' ? (
                                                <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='#362FD8' strokeWidth='3' style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden='true'>
                                                    <polyline points='6 15 12 9 18 15' />
                                                </svg>
                                            ) : (
                                                <svg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='#362FD8' strokeWidth='3' style={{ marginLeft: 4, verticalAlign: 'middle' }} aria-hidden='true'>
                                                    <polyline points='6 9 12 15 18 9' />
                                                </svg>
                                            );
                                        };
                                        const headerStyle = (active, align) => ({
                                            border: 'none', background: 'white',
                                            fontSize: '13px', fontWeight: 600,
                                            color: active ? '#362FD8' : '#1C1C1C',
                                            textTransform: 'capitalize', letterSpacing: 0,
                                            padding: '8px 5px',
                                            textAlign: align || 'left',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                        });
                                        const cols = [
                                            { key: 'kind', label: 'Type', align: 'left' },
                                            { key: 'item', label: 'Item', align: 'left' },
                                            { key: 'date', label: 'Date', align: 'left' },
                                            { key: 'points', label: 'Points', align: 'right' },
                                        ];
                                        return cols.map((c) => {
                                            const active = actSort.key === c.key;
                                            return (
                                                <th
                                                    key={c.key}
                                                    scope='col'
                                                    onClick={() => toggleActSort(c.key)}
                                                    style={headerStyle(active, c.align)}
                                                    aria-sort={active ? (actSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                                >
                                                    {c.label}
                                                    <Indicator active={active} dir={actSort.dir} />
                                                </th>
                                            );
                                        });
                                    })()}
                                    <th style={{ border: 'none', background: 'white' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {sortedActivity.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#888', border: 'none' }}>
                                            No activity yet
                                        </td>
                                    </tr>
                                ) : sortedActivity.map((entry, index) => {
                                    const chip = chipFor(entry.kind);
                                    const showPoints = entry.points !== null && entry.points !== 0;
                                    const pointsColor = entry.points > 0 ? '#079455' : entry.points < 0 ? '#D92D20' : '#1C1C1C';
                                    const pointsText = entry.points > 0 ? `+${entry.points}` : entry.points < 0 ? `${entry.points}` : '';
                                    return (
                                        <tr key={entry._id || index} className="vs-table-row">
                                            <td style={{ padding: "10px 5px" }}>
                                                <span style={{
                                                    fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px',
                                                    backgroundColor: chip.bg,
                                                    color: chip.fg,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {chip.label}
                                                </span>
                                            </td>
                                            <td style={{ padding: "10px 5px", fontSize: '14px', wordBreak: 'break-word' }}>
                                                {entry.label}
                                                {entry.kind === 'redeemed' && entry.raw?.variantName && (
                                                    <span style={{ color: '#888' }}> — {entry.raw.variantName}</span>
                                                )}
                                                {entry.kind === 'email' && entry.raw?.success === false && (
                                                    <div style={{ fontSize: '11px', color: '#D92D20', marginTop: 2 }}>
                                                        Failed: {entry.raw?.error || 'unknown'}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: "10px 5px", fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
                                                {moment(entry.date).format('MMM DD, YYYY h:mm A')}
                                            </td>
                                            <td style={{ padding: "10px 5px", fontWeight: 600, color: pointsColor, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                {showPoints ? pointsText : '—'}
                                            </td>
                                            <td style={{ padding: "10px 5px" }}>
                                                <span className="vs-action-icon" style={{ cursor: 'pointer' }} onClick={() => setSelectedEntry(entry)} title="View details">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        {/* Infinite scroll loading indicator */}
                        {loadingMore && (
                            <div style={{ textAlign: 'center', padding: '12px 0' }}>
                                <Spinner />
                            </div>
                        )}
                        {hasMoreAllocs && !loadingMore && (
                            <div style={{ textAlign: 'center', padding: '8px 0' }}>
                                <button
                                    type='button'
                                    onClick={loadMore}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: 8,
                                        padding: '8px 20px',
                                        fontSize: 13,
                                        color: '#362FD8',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        fontFamily: 'Outfit',
                                    }}
                                >
                                    Load more ({allocations.length} of {allocTotal} scans loaded)
                                </button>
                            </div>
                        )}
                    </>
                )}



                {/* Detail Modal */}
                {selectedEntry && (() => {
                    const isEarned = selectedEntry.kind === 'earned';
                    const isRedeemed = selectedEntry.kind === 'redeemed';
                    const isSignIn = selectedEntry.kind === 'signin';
                    const isEmail = selectedEntry.kind === 'email';
                    const isBonus = selectedEntry.kind === 'bonus';
                    const raw = selectedEntry.raw || {};
                    const isFulfilled = !!(raw.isFullFilled || raw.isFulfilled);
                    const fulfilledAt = raw.fullFilledDate || raw.fulfilledDate;
                    const variant = raw.variantName;
                    const carrier = (raw.shippingCarrier || '').toLowerCase();
                    const carrierLabels = { usps: 'USPS', ups: 'UPS', fedex: 'FedEx', dhl: 'DHL', other: 'Carrier' };
                    const carrierLabel = carrierLabels[carrier] || carrier;
                    const tracking = raw.trackingNumber;
                    const trackingUrls = {
                        usps: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
                        ups: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
                        fedex: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
                        dhl: (n) => `https://www.dhl.com/us-en/home/tracking/tracking-express.html?tracking-id=${encodeURIComponent(n)}`,
                    };
                    const trackingUrl = tracking && trackingUrls[carrier] ? trackingUrls[carrier](tracking) : '';
                    const addr = raw.shippingAddressSnapshot;
                    const labelStyle = { fontWeight: 600, color: '#888', fontSize: '11px', display: 'block', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.5px' };
                    const rowStyle = { marginBottom: '12px' };
                    const titleByKind = {
                        earned: 'Points earned',
                        redeemed: 'Points redeemed',
                        signin: 'Sign-in',
                        bonus: 'Welcome bonus',
                        email: 'Email sent',
                        event: 'Account event',
                    };
                    return (
                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.4)' }}
                            onClick={() => setSelectedEntry(null)}>
                            <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '460px', width: '90%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
                                onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, fontFamily: 'Outfit' }}>
                                        {titleByKind[selectedEntry.kind] || 'Activity'}
                                    </h3>
                                    <span style={{ cursor: 'pointer', fontSize: '20px', color: '#999' }} onClick={() => setSelectedEntry(null)}>&times;</span>
                                </div>
                                <div style={{ fontFamily: 'Outfit', fontSize: '14px' }}>
                                    {(isEarned || isRedeemed) && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>{isEarned ? 'Product scanned' : 'Reward redeemed'}</label>
                                            <span>{selectedEntry.label}{variant ? <span style={{ color: '#888' }}> — {variant}</span> : null}</span>
                                        </div>
                                    )}
                                    {(isSignIn || isBonus) && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>What</label>
                                            <span>{selectedEntry.label}</span>
                                        </div>
                                    )}
                                    {isEmail && (
                                        <>
                                            <div style={rowStyle}>
                                                <label style={labelStyle}>Type</label>
                                                <span>{selectedEntry.label}</span>
                                            </div>
                                            {raw.to && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>To</label>
                                                    <span style={{ wordBreak: 'break-all' }}>{raw.to}</span>
                                                </div>
                                            )}
                                            {raw.subject && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Subject</label>
                                                    <span>{raw.subject}</span>
                                                </div>
                                            )}
                                            <div style={rowStyle}>
                                                <label style={labelStyle}>Status</label>
                                                <span style={{
                                                    fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '4px',
                                                    backgroundColor: raw.success ? '#dcfce7' : '#fef2f2',
                                                    color: raw.success ? '#079455' : '#D92D20',
                                                }}>
                                                    {raw.success ? 'Delivered' : 'Failed'}
                                                </span>
                                                {raw.resent && (
                                                    <span style={{ marginLeft: 8, fontSize: 11, color: '#6b7280' }}>· re-sent by admin</span>
                                                )}
                                            </div>
                                            {raw.error && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Error</label>
                                                    <span style={{ color: '#D92D20', fontSize: 13 }}>{raw.error}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {showPointsRow(selectedEntry) && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>Points</label>
                                            <span style={{ fontWeight: 600, color: selectedEntry.points > 0 ? '#079455' : selectedEntry.points < 0 ? '#D92D20' : '#1C1C1C' }}>
                                                {selectedEntry.points > 0 ? `+${selectedEntry.points}` : selectedEntry.points}
                                            </span>
                                        </div>
                                    )}
                                    <div style={rowStyle}>
                                        <label style={labelStyle}>Date</label>
                                        <span>{moment(selectedEntry.date).format('MMMM DD, YYYY · h:mm:ss A')}</span>
                                    </div>

                                    {isEarned && raw.codeId && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>Code scanned</label>
                                            <span style={{ fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#f5f5f5', padding: '2px 8px', borderRadius: '4px', wordBreak: 'break-all' }}>{raw.codeId}</span>
                                        </div>
                                    )}
                                    {isEarned && raw.serialNumber && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>Serial</label>
                                            <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{raw.serialNumber}</span>
                                        </div>
                                    )}
                                    {(isEarned || isSignIn) && raw.ip && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>IP address</label>
                                            <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{raw.ip}</span>
                                        </div>
                                    )}
                                    {isEarned && raw.location?.city && (
                                        <div style={rowStyle}>
                                            <label style={labelStyle}>Location</label>
                                            <span>{[raw.location.city, raw.location.region, raw.location.country].filter(Boolean).join(', ')}</span>
                                        </div>
                                    )}

                                    {isRedeemed && (
                                        <>
                                            {raw.orderNumber && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Order number</label>
                                                    <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>#{raw.orderNumber}</span>
                                                </div>
                                            )}
                                            <div style={rowStyle}>
                                                <label style={labelStyle}>Status</label>
                                                <span style={{
                                                    fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '4px',
                                                    backgroundColor: isFulfilled ? '#dcfce7' : '#fef3cd',
                                                    color: isFulfilled ? '#079455' : '#856404'
                                                }}>
                                                    {isFulfilled ? 'Fulfilled' : 'Pending'}
                                                </span>
                                            </div>
                                            {isFulfilled && raw.fulfillmentMethod && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Method</label>
                                                    <span>{raw.fulfillmentMethod === 'in_person' ? 'Picked up in person' : 'Shipped'}{carrierLabel && raw.fulfillmentMethod === 'shipped' ? ` · ${carrierLabel}` : ''}</span>
                                                </div>
                                            )}
                                            {isFulfilled && tracking && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Tracking</label>
                                                    {trackingUrl ? (
                                                        <a href={trackingUrl} target='_blank' rel='noopener noreferrer' style={{ color: '#362FD8', wordBreak: 'break-all' }}>{tracking}</a>
                                                    ) : (
                                                        <span style={{ wordBreak: 'break-all' }}>{tracking}</span>
                                                    )}
                                                </div>
                                            )}
                                            {isFulfilled && fulfilledAt && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Fulfilled</label>
                                                    <span>{moment(fulfilledAt).format('MMMM DD, YYYY · h:mm A')}</span>
                                                </div>
                                            )}
                                            {addr && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Shipping address</label>
                                                    <div style={{ lineHeight: 1.5 }}>
                                                        <div>{[addr.firstName, addr.lastName].filter(Boolean).join(' ')}</div>
                                                        {(addr.shippingAddress || addr.address1) && <div>{addr.shippingAddress || addr.address1}</div>}
                                                        {addr.address2 && <div>{addr.address2}</div>}
                                                        <div>{[addr.city, addr.state].filter(Boolean).join(', ')}{addr.zip ? ` ${addr.zip}` : ''}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {raw.customerEmail && (
                                                <div style={rowStyle}>
                                                    <label style={labelStyle}>Order email</label>
                                                    <span style={{ wordBreak: 'break-all' }}>{raw.customerEmail}</span>
                                                </div>
                                            )}
                                            {raw.notes && (
                                                <div style={{ ...rowStyle, padding: '10px 12px', background: '#FFF7E6', border: '1px solid #F5C046', borderRadius: 6 }}>
                                                    <label style={{ ...labelStyle, color: '#8A5A00' }}>Internal notes (staff only)</label>
                                                    <div style={{ whiteSpace: 'pre-wrap' }}>{raw.notes}</div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}

            </div>
        </div>
    );
}

// Helper for the detail modal — only show the points row when the
// event has a meaningful point delta. Sign-ins + emails don't.
function showPointsRow(entry) {
    return entry && entry.points !== null && entry.points !== 0;
}

export default ViewPointHistory
